import type { FileRecordActionHandlerDeps } from "@/object-actions/handlers/fileRecord";
import type {
  Canvas,
  FileRecord,
  Folder,
  InboxItem,
} from "@cohesive/api-client";
import React from "react";
import type { ResourceFolderActionDeps } from "../components/WorkspaceResourceFolderCardItem";
import type { BrowserItem } from "../home/utils/homeBrowserTypes";
import { getItemName } from "../types";
import { getDirectChildren } from "../utils/folderPaths";
import {
  FileBrowserFolderRow,
  ResourceBrowserFolderRow,
} from "./BrowserFolderRow";

/** Matches `buildFolderTrail`'s cycle cap — a cyclic parent chain stops
 *  recursing instead of blowing the stack. */
const MAX_TREE_DEPTH = 64;

/** Muted line under an expanded empty folder — the left rail's
 *  "No files in folder." treatment, indented to the children's depth. */
function TreeEmptyState({ label, depth }: { label: string; depth: number }) {
  return (
    <p
      className="px-2.5 py-2 text-sm text-n-40"
      style={depth > 0 ? { paddingLeft: `${10 + depth * 20}px` } : undefined}
    >
      {label}
    </p>
  );
}

export interface TreeExpansion {
  isOpen: (key: string) => boolean;
  onToggle: (key: string) => void;
}

interface ResourceTreeSharedProps {
  /** Owning workspace — folder rows are move drop targets scoped to it. */
  workspaceId: string;
  folders: Folder[];
  expansion: TreeExpansion;
  depth?: number;
  /** Sorts the loose items at each level per the page's active sort. */
  sortItems: (items: BrowserItem[]) => BrowserItem[];
  renderRow: (item: BrowserItem, depth: number) => React.ReactNode;
  folderDeps: (folder: Folder) => ResourceFolderActionDeps;
  folderChildCount: (folder: Folder) => number;
}

/**
 * Inline-expandable canvas folder tree for the browser's list view: at each
 * level, name-sorted child folders (each recursing) then the canvases filed
 * directly at that level. Folders-first mirrors the rail's tree and reads
 * like a file manager.
 */
export function CanvasBrowserTree({
  canvases,
  parentFolderId = null,
  workspaceId,
  folders,
  expansion,
  depth = 0,
  sortItems,
  renderRow,
  folderDeps,
  folderChildCount,
}: ResourceTreeSharedProps & {
  canvases: Canvas[];
  parentFolderId?: string | null;
}) {
  if (depth >= MAX_TREE_DEPTH) return null;
  const childFolders = folders
    .filter((f) => (f.parent_folder_id ?? null) === parentFolderId)
    .sort((a, b) => a.name.localeCompare(b.name));
  const items = sortItems(
    canvases
      .filter((c) => (c.folder_id ?? null) === parentFolderId)
      .map((data): BrowserItem => ({ kind: "canvas", data })),
  );

  // Only reachable nested (an expanded empty folder) — the browser swaps an
  // empty root level for the folder-scoped page empty state instead.
  if (childFolders.length === 0 && items.length === 0) {
    return <TreeEmptyState label="No canvases in folder." depth={depth} />;
  }

  return (
    <>
      {childFolders.map((folder) => (
        <React.Fragment key={folder.id}>
          <ResourceBrowserFolderRow
            folder={folder}
            workspaceId={workspaceId}
            childCount={folderChildCount(folder)}
            open={expansion.isOpen(folder.id)}
            onToggle={() => expansion.onToggle(folder.id)}
            depth={depth}
            deps={folderDeps(folder)}
          />
          {expansion.isOpen(folder.id) && (
            <CanvasBrowserTree
              canvases={canvases}
              parentFolderId={folder.id}
              workspaceId={workspaceId}
              folders={folders}
              expansion={expansion}
              depth={depth + 1}
              sortItems={sortItems}
              renderRow={renderRow}
              folderDeps={folderDeps}
              folderChildCount={folderChildCount}
            />
          )}
        </React.Fragment>
      ))}
      {items.map((item) => renderRow(item, depth))}
    </>
  );
}

/** Chat flavor — identical shape over `flow_session.folder_id`. */
export function ChatBrowserTree({
  chats,
  parentFolderId = null,
  workspaceId,
  folders,
  expansion,
  depth = 0,
  sortItems,
  renderRow,
  folderDeps,
  folderChildCount,
}: ResourceTreeSharedProps & {
  chats: InboxItem[];
  parentFolderId?: string | null;
}) {
  if (depth >= MAX_TREE_DEPTH) return null;
  const childFolders = folders
    .filter((f) => (f.parent_folder_id ?? null) === parentFolderId)
    .sort((a, b) => a.name.localeCompare(b.name));
  const items = sortItems(
    chats
      .filter((c) => (c.flow_session?.folder_id ?? null) === parentFolderId)
      .map((data): BrowserItem => ({ kind: "chat", data })),
  );

  if (childFolders.length === 0 && items.length === 0) {
    return <TreeEmptyState label="No chats in folder." depth={depth} />;
  }

  return (
    <>
      {childFolders.map((folder) => (
        <React.Fragment key={folder.id}>
          <ResourceBrowserFolderRow
            folder={folder}
            workspaceId={workspaceId}
            childCount={folderChildCount(folder)}
            open={expansion.isOpen(folder.id)}
            onToggle={() => expansion.onToggle(folder.id)}
            depth={depth}
            deps={folderDeps(folder)}
          />
          {expansion.isOpen(folder.id) && (
            <ChatBrowserTree
              chats={chats}
              parentFolderId={folder.id}
              workspaceId={workspaceId}
              folders={folders}
              expansion={expansion}
              depth={depth + 1}
              sortItems={sortItems}
              renderRow={renderRow}
              folderDeps={folderDeps}
              folderChildCount={folderChildCount}
            />
          )}
        </React.Fragment>
      ))}
      {items.map((item) => renderRow(item, depth))}
    </>
  );
}

/**
 * File flavor — path-based: folders are records whose path ends in `/`,
 * expansion keys on the folder path so it survives renames of siblings.
 */
export function FilesBrowserTree({
  records,
  parentPath = "",
  workspaceId,
  expansion,
  depth = 0,
  suppressedRecordIds,
  sortItems,
  renderRow,
  fileFolderDeps,
  folderChildCount,
}: {
  records: FileRecord[];
  parentPath?: string;
  /** Owning workspace — folder rows are move drop targets scoped to it. */
  workspaceId: string;
  expansion: TreeExpansion;
  depth?: number;
  suppressedRecordIds?: Set<string>;
  sortItems: (items: BrowserItem[]) => BrowserItem[];
  renderRow: (item: BrowserItem, depth: number) => React.ReactNode;
  fileFolderDeps: FileRecordActionHandlerDeps;
  folderChildCount: (record: FileRecord) => number;
}) {
  if (depth >= MAX_TREE_DEPTH) return null;
  const direct = getDirectChildren(records, parentPath).filter(
    (r) => r.status !== "pending" && r.status !== "deleted",
  );
  const folders = direct
    .filter((r) => r.path.endsWith("/"))
    .sort((a, b) => getItemName(a).localeCompare(getItemName(b)));
  const items = sortItems(
    direct
      .filter((r) => !r.path.endsWith("/") && !suppressedRecordIds?.has(r.id))
      .map((data): BrowserItem => ({ kind: "file", data })),
  );

  if (folders.length === 0 && items.length === 0) {
    return <TreeEmptyState label="No files in folder." depth={depth} />;
  }

  return (
    <>
      {folders.map((record) => (
        <React.Fragment key={record.id}>
          <FileBrowserFolderRow
            record={record}
            workspaceId={workspaceId}
            childCount={folderChildCount(record)}
            open={expansion.isOpen(record.path)}
            onToggle={() => expansion.onToggle(record.path)}
            depth={depth}
            handlerDeps={fileFolderDeps}
          />
          {expansion.isOpen(record.path) && (
            <FilesBrowserTree
              records={records}
              parentPath={record.path}
              workspaceId={workspaceId}
              expansion={expansion}
              depth={depth + 1}
              suppressedRecordIds={suppressedRecordIds}
              sortItems={sortItems}
              renderRow={renderRow}
              fileFolderDeps={fileFolderDeps}
              folderChildCount={folderChildCount}
            />
          )}
        </React.Fragment>
      ))}
      {items.map((item) => renderRow(item, depth))}
    </>
  );
}
